import { get, post } from '@/lib/api/client'
import {
  PaginationParamsSchema,
  ProgrammeFilterParamsSchema,
  type Programme,
  type ProgrammeDetail,
  type ProgrammeFilterParams,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class ProgrammeService {
  /**
   * Filter programmes with pagination (Public endpoint)
   * POST /programmes/public/filter?page={page}&size={size}
   */
  async getProgrammes(
    params: PaginationParams = { page: 1, size: 10 },
    filters: ProgrammeFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Programme>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = ProgrammeFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Programme>>>(
      `programmes/public/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Search programmes (Public endpoint)
   * GET /programmes/public/search?searchBy={searchTerm}&size={size}&page={page}
   */
  async searchProgrammes(
    searchTerm: string,
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<Programme>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const query = new URLSearchParams({
      searchBy: searchTerm,
      size: validatedParams.size.toString(),
      page: validatedParams.page.toString()
    }).toString()

    return await get<ApiResponse<PaginatedResponse<Programme>>>(
      `programmes/public/search?${query}`
    )
  }

  /**
   * Get programme by ID (Public endpoint)
   * GET /programmes/public/{programmeId}
   */
  async getProgrammeById(programmeId: string): Promise<ApiResponse<ProgrammeDetail>> {
    return await get<ApiResponse<ProgrammeDetail>>(
      `programmes/public/${programmeId}`
    )
  }

  /**
   * Get landing page programmes
   * POST /programmes/public/filter with landingPageAfsaProgramme filter
   */
  async getLandingPageProgrammes(
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<Programme>>> {
    return this.getProgrammes(params, {
      status: 'PUBLISH',
      landingPageAfsaProgramme: true
    })
  }

  /**
   * Get all published programmes
   * POST /programmes/public/filter with status filter
   */
  async getPublishedProgrammes(
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<Programme>>> {
    return this.getProgrammes(params, {
      status: 'PUBLISH'
    })
  }

  /**
   * Get all programmes (public endpoint)
   * GET /programmes/public/all
   */
  async getAllProgrammes(): Promise<ApiResponse<Programme[]>> {
    return await get<ApiResponse<Programme[]>>(
      'programmes/public/all'
    )
  }

  /**
   * Get programme highlights
   * GET /programmes/public/highlights?type={type}
   */
  async getProgrammeHighlights(type: string = 'HOME_PAGE_AFSA_PROGRAMME'): Promise<ApiResponse<Programme[]>> {
    return await get<ApiResponse<Programme[]>>(
      `programmes/public/highlights?type=${type}`
    )
  }
}

// Export singleton instance
export const programmeService = new ProgrammeService()

// Export default
export default programmeService
